import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserAccountService } from '../user-account/user-account.service';

@Injectable({
  providedIn: 'root',
})
export class SearchServiceStatusService {
  private searchServicesSubject = new BehaviorSubject<any>(null);
  public searchServices$ = this.searchServicesSubject.asObservable();

  private creditCostsSubject = new BehaviorSubject<Record<string, number> | null>(
    null,
  );
  public creditCosts$ = this.creditCostsSubject.asObservable();

  constructor(private userAccountService: UserAccountService) {
    this.loadSearchServices();
  }

  /**
   * Load search services from user profile API
   */
  loadSearchServices(): void {
    this.userAccountService.getUserProfile().subscribe({
      next: (response) => {
        const searchServices = response?.data?.search_services || {};
        this.searchServicesSubject.next(searchServices);
        const creditCosts = response?.data?.credit_costs ?? null;
        this.creditCostsSubject.next(creditCosts);
      },
      error: (error) => {
        console.error('Failed to load search services:', error);
        this.creditCostsSubject.next(null);
        // Default to all services enabled if API fails
        this.searchServicesSubject.next({
          osint: true,
          basic: true,
          leak: true,
          advance_leak: true,
          name: true,
          pan: true,
          vehicle: true,
          face: true,
          face_compare: true,
          video_scanner: true,
          virtual: true,
          web_capture: true,
          link_tracking: true,
          link_analysis: true,
          gst: true,
          din: true,
          mobile_pan_search: true,
          phonepe_fastag: true,
          phonepe_upi: true,
          osint_ai_analysis: true,
        });
      },
    });
  }

  /**
   * Check if a specific service is enabled
   * @param serviceName - The service name (osint, leak, name, pan, vehicle, face, virtual, link_tracking)
   * @returns true if service is enabled, false otherwise
   */
  isServiceEnabled(serviceName: string): boolean {
    const searchServices = this.searchServicesSubject.value;
    if (!searchServices) {
      // If services not loaded yet, assume enabled
      return true;
    }
    return searchServices[serviceName] === true;
  }

  /**
   * OSINT search AI Analysis tab. Missing key defaults to enabled.
   */
  isOsintAiAnalysisEnabled(): boolean {
    const searchServices = this.searchServicesSubject.value;
    if (!searchServices) {
      return true;
    }
    return searchServices.osint_ai_analysis !== false;
  }

  /**
   * Get current search services object
   */
  getSearchServices(): any {
    return this.searchServicesSubject.value;
  }

  /** Single cost from `/me` data.credit_costs for a given API key. */
  resolveCreditCost(
    creditCosts: Record<string, number> | null | undefined,
    key: string,
  ): number | null {
    const n = creditCosts?.[key];
    return typeof n === 'number' ? n : null;
  }

  /**
   * Refresh search services from API
   */
  refreshSearchServices(): void {
    this.loadSearchServices();
  }
}
